import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldDescription,
  FieldError,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authApi, markCompanySelected } from "@/lib/api";
import { SubscriptionSelectorModal } from "./subscription-selector-modal";
import { useAuth } from "../context/auth-context";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { LoginSchema, type LoginFormData } from "../schemas/login-schema";
import { Loader2 } from "lucide-react";
import { AuthLayout } from "./auth-layout";
import {
  consumeQrRedirectPath,
  qrRedirectPathCarriesTenant,
  readQrRedirectPath,
} from "../lib/qr-redirect-path";

export function LoginForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setToken, setSelectedCompanyId } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSubscriptionSelector, setShowSubscriptionSelector] =
    useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    setSelectedCompanyId(null);
    try {
      localStorage.removeItem("selectedCompanyId");
    } catch (error) {
      console.warn("Error al limpiar localStorage:", error);
    }
  }, [setSelectedCompanyId]);

  const storedQRRedirectPath = readQrRedirectPath() ?? undefined;
  const storedQRDispatchGuideId =
    sessionStorage.getItem("qrDispatchGuideId") || undefined;

  const from =
    (location.state as { from?: { pathname?: string } })?.from?.pathname ||
    storedQRRedirectPath ||
    "/";

  const handleLoginSuccess = async () => {
    const qrDispatchGuideId =
      storedQRDispatchGuideId || sessionStorage.getItem("qrDispatchGuideId");
    const qrRedirectPath = readQrRedirectPath() || from;

    if (qrDispatchGuideId && qrRedirectPath) {
      try {
        const { dispatchGuideApi } =
          await import("@/features/dispatch-guide/services/dispatch-guide-api");

        const userCompanies = await authApi.companies();
        for (const company of userCompanies as Array<{
          id: string;
          subscriptionId: string;
        }>) {
          try {
            const ctx = await dispatchGuideApi.getContextByIdForCompany(
              qrDispatchGuideId,
              company.id,
            );
            const guideCompanyId = ctx.companyId;
            if (guideCompanyId) {
              const subscriptionId = company.subscriptionId;
              if (subscriptionId) {
                localStorage.setItem(
                  "selectedSubscriptionId",
                  subscriptionId,
                );
              }
              setSelectedCompanyId(guideCompanyId);
              localStorage.setItem("selectedCompanyId", guideCompanyId);
              markCompanySelected();
              sessionStorage.removeItem("qrDispatchGuideId");
              consumeQrRedirectPath();
              navigate(qrRedirectPath, { replace: true });
              return;
            }
          } catch {
            // Intentar con la siguiente empresa
          }
        }
      } catch (err) {
        console.error(
          "Error al obtener el detalle de movimiento para selección automática:",
          err,
        );
      }

      setShowSubscriptionSelector(true);
      return;
    }

    const pendingQrPath = readQrRedirectPath();
    if (pendingQrPath && qrRedirectPathCarriesTenant(pendingQrPath)) {
      consumeQrRedirectPath();
      navigate(pendingQrPath, { replace: true });
      return;
    }

    setShowSubscriptionSelector(true);
  };

  async function onSubmit(values: LoginFormData) {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const res = await authApi.login({
        email: values.email,
        password: values.password,
      });

      if (
        !res.token ||
        typeof res.token !== "string" ||
        res.token.trim() === ""
      ) {
        setErrorMessage("Token no recibido del servidor");
        setSubmitting(false);
        return;
      }

      setToken(res.token);
      await handleLoginSuccess();
    } catch (err: unknown) {
      const message =
        (err as { message?: string }).message || "Error al iniciar sesión";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">Correo electrónico</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="admin@wmpartner.com"
              required
              autoComplete="email"
              {...form.register("email")}
              aria-invalid={!!form.formState.errors.email}
            />
            <FieldError errors={[form.formState.errors.email]} />
          </Field>

          <Field>
            <div className="flex items-center">
              <FieldLabel htmlFor="password">Contraseña</FieldLabel>
              <Link
                to="/forgot-password"
                className="ml-auto text-sm text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              {...form.register("password")}
              aria-invalid={!!form.formState.errors.password}
            />
            <FieldError errors={[form.formState.errors.password]} />
          </Field>

          {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}

          <Field>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </Field>

          <FieldDescription className="text-center">
            ¿No tienes una cuenta?{" "}
            <a
              href="#"
              className="underline underline-offset-2 hover:text-primary"
            >
              Regístrate
            </a>
          </FieldDescription>
        </FieldGroup>
      </form>

      <SubscriptionSelectorModal
        open={showSubscriptionSelector}
        onOpenChange={setShowSubscriptionSelector}
        redirectTo={from}
      />
    </AuthLayout>
  );
}
